import {
    parsedQueryString,
    screenshotModeEnabled,
} from '../utilities/utilities';
import ScrollLockAnimation from './scroll-lock-animation';
import CircleScreensBackground from './circle-screens-background';
import CircleScreen from './circle-screen';

const settings = {
    test: {
        enabled: false,

        // Plays through the timeline
        play: false,
        timeScale: 2,

        // If play is set to false, this will jump to a specific screen
        skipToSlide: 3,
    },

    dataAttributes: {
        screenType: 'data-screen-type',
    },

    classNames: {
        container: 'hero-circles',
    },

    selectors: {
        circleScreensBackground: '.circle-screen-background',
        screenContainers: 'div[data-screen-type]',
    },

    screenshotModeParameters: {
        screenIndex: 'hero-index',
    },

    animation: {
        scrollDistancePerSecond: 600,
    },
};

const screenTypes = {
    intro: 1,
    circleStart: 2,
    circleMiddle: 3,
    circleEnd: 4,
};

export default class HeroCircles {
    constructor(
        container,
        useScrollLockAnimation = true,
    ) {
        // Find our circle background elements
        this.background = new CircleScreensBackground(
            container.querySelector(
                settings.selectors.circleScreensBackground,
            ),
        );

        // Find our screens and create timeline
        this.screens = [];
        this.timeline = gsap.timeline({
            paused: true,
        });

        const screenContainers = container
            .querySelectorAll(settings.selectors.screenContainers);
        for (let i=0; i<screenContainers.length; i++) {
            const screen = new Screen(
                screenContainers[i],
                this.background,
                this.timeline,
                useScrollLockAnimation !== true,
            );

            this.screens.push(screen);
        }

        if (useScrollLockAnimation) {
            // Determine the amount of scroll distance in which we'll transition between
            // the timestamp at which the first screen is fully visible and the end of
            // the animation - first, determine the duration between these two points in
            // the timeline, in seconds
            let scrollLockDistance = this.timeline.duration();

            // Next, convert seconds to pixels
            scrollLockDistance *= settings.animation.scrollDistancePerSecond;
            scrollLockDistance = Math.round(scrollLockDistance);

            // Create our scroll lock animation now that we have the necessary
            // parameters calculated
            this.scrollLockAnimation = new HeroCirclesScrollLockAnimation(
                container,
                scrollLockDistance,
                this.timeline,
            );
        }

        // Testing
        if (settings.test.enabled) {
            if (settings.test.play) {
                this.timeline.timeScale(settings.test.timeScale);
                this.timeline.play();
            } else {
                this.timeline.seek(
                    this.screens[settings.test.skipToSlide]
                        .timestamps
                        .visible);
            }

            // Stop here
            return;
        }

        // Check if screenshot mode is enabled
        if (screenshotModeEnabled) {
            // Check if a screen index was provided
            let screenIndex = parsedQueryString[
                settings
                    .screenshotModeParameters
                    .screenIndex
            ];

            screenIndex = parseInt(screenIndex);
            if (
                !isNaN(screenIndex) &&
                screenIndex >= 0 &&
                screenIndex < this.screens.length
            ) {
                // Translate the timestamp associated with the screen
                // at the specified index to a 0-1 progress value
                const progress =
                    (
                        this.screens[screenIndex]
                            .timestamps
                            .visible
                    ) /
                    this.timeline.duration();

                // Move the animation to the calculated progress value
                this.setAnimationProgress(progress);
            }
        }
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new HeroCircles(elements[i]);
        }
    }
}

/*
    Internal classes and functions
*/

class HeroCirclesScrollLockAnimation extends ScrollLockAnimation {
    constructor(
        container,
        scrollLockDistance,
        timeline,
    ) {
        // Call base class
        super(container, scrollLockDistance);

        // Record a reference to the timeline we're animating during scroll
        this.timeline = timeline;
    }

    /**
     * Signals that we should update our position within the animation;
     * "progress" indicates how far the user has scrolled through
     * the animation when the container is fully onscreen;
     * See the base class for details
     * @param {number} progress
     */
    setAnimationProgress(progress) {
        // Call base class
        super.setAnimationProgress(progress);

        // Update position in the timeline
        this.timeline.progress(progress);
    }
}

class Screen extends CircleScreen {
    /**
     * @constructor
     * @param {HTMLElement} container
     * @param {CircleScreensBackground} background
     * @param {object} timeline
     * @param {boolean} adjustTimingsForLinearPlayback
     */
    constructor(
        container,
        background,
        timeline,
        adjustTimingsForLinearPlayback = false,
    ) {
        // Call base class
        super(container);

        // Determine screen type
        this.screenType = container
            .getAttribute(settings.dataAttributes.screenType);
        if (this.screenType.startsWith('mid-')) {
            this.screenType = screenTypes.circleMiddle;
        } else {
            switch (this.screenType) {
                case 'intro':
                    this.screenType = screenTypes.intro;
                    break;

                case 'start':
                    this.screenType = screenTypes.circleStart;
                    break;

                case 'end':
                    this.screenType = screenTypes.circleEnd;
                    break;

                default:
                    throw new Error(`Unrecognized screen type: ${this.screenType}`);
            }
        }

        // Record start timestamp
        this.timestamps = {
            start: timeline.duration(),
        };

        // Setup timing adjustments for default (scrolljacking) vs.
        // altered for linear time-based playback
        const timingAdjustments = {
            screenDelayAdjustment: 0,
            endScreenSmallCircleInitialDelay: 0.25,
            endScreenSmallCircleMidDelay: 0.25,
        };

        if (adjustTimingsForLinearPlayback) {
            if (typeof this.parameters.screenPause == 'number') {
                timingAdjustments.screenDelayAdjustment = (
                    this.parameters.screenPause
                );
            }

            timingAdjustments.endScreenSmallCircleInitialDelay = 0;
            timingAdjustments.endScreenSmallCircleMidDelay = 0;
        }

        // Add animation to timeline
        switch (this.screenType) {
            case screenTypes.intro:
                // Record visible timestamp - intro starts visible
                this.timestamps.visible = timeline.duration();

                // Fade copy out
                timeline.to(
                    this.container,
                    1.0,
                    {
                        ease: 'power2.in',
                        opacity: 0,
                        scale: 0.95,
                        pointerEvents: 'none',
                    },
                    this.timestamps.visible,
                );
                break;

            case screenTypes.circleStart:
                // Background transition (first appearance)
                background.setOpacity(
                    CircleScreensBackground.elementTypes.whiteCircle,
                    1,
                    timeline,
                    this.timestamps.start,
                    1.0,
                    'power2.out');

                background.transitionScale(
                    this.parameters.bgScale,
                    timeline,
                    this.timestamps.start,
                    1.0,
                    'power2.out');

                // Foreground transition
                this.show(
                    timeline,
                    this.timestamps.start + 0.25);

                // Record visible timestamp
                this.timestamps.visible = timeline.duration();

                // Foreground hide
                this.hide(
                    timeline,
                    (
                        this.timestamps.visible +
                        timingAdjustments.screenDelayAdjustment
                    ),
                );
                break;

            case screenTypes.circleMiddle:
                // Background transition (changing sizes)
                {
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 0.5;

                    background.transitionScale(
                        this.parameters.bgScale,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');
                }

                // Foreground transition
                this.show(
                    timeline,
                    this.timestamps.start);

                // Record visible timestamp
                this.timestamps.visible = timeline.duration();

                // Foreground hide
                this.hide(
                    timeline,
                    (
                        this.timestamps.visible +
                        timingAdjustments.screenDelayAdjustment
                    ),
                );
                break;

            // screenTypes.circleEnd
            default:
                // Background transition (changing sizes + introducing colored circles)
                {
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 0.5;

                    background.transitionScale(
                        this.parameters.bgScale,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');

                    // Colored circles - these arrays define the order
                    // in which the circles appear
                    const smallCircleTypes = [
                        CircleScreensBackground.elementTypes.blueCircle,
                        CircleScreensBackground.elementTypes.yellowCircle,
                        CircleScreensBackground.elementTypes.redCircle,
                    ];

                    const smallCircleOffsets = [
                        this.parameters.blueOffset,
                        this.parameters.yellowOffset,
                        this.parameters.redOffset,
                    ];

                    smallCircleTypes.forEach((smallCircleType, index) => {
                        // Delay between circles
                        const circleStartTime = backgroundStartTime +
                            timingAdjustments.endScreenSmallCircleInitialDelay +
                            (
                                index *
                                timingAdjustments.endScreenSmallCircleMidDelay
                            );

                        background.transitionSmallCircle(
                            smallCircleType,
                            1,
                            smallCircleOffsets[index],
                            timeline,
                            circleStartTime,
                            1.5,
                            'power1.out',
                        );
                    });
                }

                // Foreground transition
                this.show(
                    timeline,
                    this.timestamps.start);

                // Record visible timestamp
                this.timestamps.visible = timeline.duration();
                break;
        }

        // Record end timestamp
        this.timestamps.end = timeline.duration();
    }
}
